// Global dosage phrases, mirroring migration 0003. In cloud mode these arrive
// through the `reference` sync stream; demo mode has no sync, so it seeds them
// locally. Codes match the migration exactly so a demo clinic that is later
// connected does not end up with duplicates under different codes.
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import { newId } from '@/lib/db/db';

type Row = [kind: string, code: string, en: string, my: string, timesPerDay: number | null, order: number];

export const GLOBAL_PHRASES: Row[] = [
  ['frequency', 'OD', 'Once a day', 'တစ်နေ့ ၁ ကြိမ်', 1, 10],
  ['frequency', 'BD', 'Twice a day', 'တစ်နေ့ ၂ ကြိမ်', 2, 20],
  ['frequency', 'TDS', 'Three times a day', 'တစ်နေ့ ၃ ကြိမ်', 3, 30],
  ['frequency', 'QID', 'Four times a day', 'တစ်နေ့ ၄ ကြိမ်', 4, 40],
  ['frequency', 'HS', 'At bedtime', 'အိပ်ရာဝင်ချိန်', 1, 50],
  ['frequency', 'PRN', 'When needed', 'လိုအပ်သည့်အခါ', null, 60],
  ['frequency', 'STAT', 'Immediately, once', 'ချက်ချင်း တစ်ကြိမ်', null, 70],
  ['instruction', 'AC', 'Before meals', 'အစားမစားမီ', null, 10],
  ['instruction', 'PC', 'After meals', 'အစားစားပြီးနောက်', null, 20],
  ['instruction', 'WATER', 'With plenty of water', 'ရေများများနှင့်သောက်ပါ', null, 30],
  ['instruction', 'FULL', 'Finish the whole course', 'ဆေးအကုန်သောက်ပါ', null, 40],
  ['instruction', 'SHAKE', 'Shake well before use', 'အသုံးမပြုမီ ကောင်းစွာ လှုပ်ပါ', null, 50],
  ['instruction', 'EXT', 'For external use only', 'အပြင်ဘက်မှသာ သုံးရန်', null, 60],
  ['route', 'PO', 'By mouth', 'သောက်ဆေး', null, 10],
  ['route', 'TOP', 'Apply to skin', 'လိမ်းဆေး', null, 20],
  ['route', 'IM', 'Intramuscular', 'ကြွက်သားထဲထိုး', null, 30],
  ['route', 'IV', 'Intravenous', 'သွေးကြောထဲထိုး', null, 40],
  ['route', 'INH', 'Inhaled', 'ရှူဆေး', null, 60],
  ['route', 'EYE', 'Eye drops', 'မျက်စိခတ်ဆေး', null, 70],
  ['dose_unit', 'tab', 'tablet', 'လုံး', null, 10],
  ['dose_unit', 'cap', 'capsule', 'တောင့်', null, 20],
  ['dose_unit', 'ml', 'ml', 'မီလီလီတာ', null, 30],
  ['dose_unit', 'tsp', 'teaspoon', 'လက်ဖက်ရည်ဇွန်း', null, 40],
  ['dose_unit', 'drop', 'drop', 'စက်', null, 50],
  ['dose_unit', 'puff', 'puff', 'ရှူ', null, 60],
  ['dose_unit', 'sachet', 'sachet', 'ထုပ်', null, 70],
  ['dose_unit', 'app', 'application', 'ကြိမ်', null, 80],
];

export async function seedGlobalPhrases(db: AbstractPowerSyncDatabase): Promise<void> {
  const have = await db.getOptional<{ n: number }>('select count(*) as n from dosage_phrases where clinic_id is null');
  if ((have?.n ?? 0) > 0) return;
  await db.writeTransaction(async (tx) => {
    for (const [kind, code, en, mm, times, order] of GLOBAL_PHRASES) {
      await tx.execute(
        `insert into dosage_phrases (id, clinic_id, kind, code, text_en, text_my, times_per_day, sort_order, active) values (?, null, ?, ?, ?, ?, ?, ?, 1)`,
        [newId(), kind, code, en, mm, times, order]);
    }
  });
}
